import { palette as basePalette } from '@repo/ui';
import { STUDIO_THEME_DEFAULTS } from '@repo/types';
import { brandRamp } from './brandRamp';
import { mutedTextOn, surfaceOn } from 'utils/contrast';

/*
  VRG-720 — every `theme?.<token> ||` fallback below reads `STUDIO_THEME_DEFAULTS`
  rather than its own literal. Studio's swatches read the same object, so what the
  operator is shown for an UNSET token is what this file renders. Two of the four had
  already drifted (`bg_color` white vs #F7F7F8, `secondary_color` black vs the light
  hairline); `border.subtle` is unchanged and still serves non-brand borders.
*/

export type Theme = {
  border_radius: string;
  primary_color: string;
  secondary_color: string;
  // Body / paragraph text color (VERA-269). Headings follow primary_color.
  text_color?: string;
  bg_color: string;
  // Body font; headings use title_font (falls back to font). (VERA-270)
  font: string;
  title_font?: string;
  /**
   * The brand's own mark (VERA-304). Rides with the theme tokens because it is
   * the same class of asset and arrives from the same public endpoint —
   * `GET /companies/public/{id}/settings/style` merges it in from
   * `companies.logo`. ABSENT (never '' or null) when the brand has none, so a
   * single `if (!logo)` is the whole fallback. Consumed by the loader; see
   * `components/UI/LogoLoader.tsx`.
   */
  logo?: string;
};

import type { PaletteOptions } from '@mui/material';

export const palette = (theme: Theme | null): Partial<PaletteOptions> =>
  ({
  ...basePalette,
  /**
   * Derived from the tenant's own primary, NOT inherited from `@repo/ui`.
   *
   * Spreading `basePalette` brings in Vera's purple `brand` ramp, and neither
   * theme layer overrode it — so `@repo/ui`'s `Input`, which paints its hover
   * and focus border from `brand[600]`, focus-ringed Vera purple in every
   * tenant's own site. Deriving here fixes that for every `brand[*]` consumer
   * at once, ours and `@repo/ui`'s. See `./brandRamp.ts`.
   *
   * Must stay AFTER the `...basePalette` spread.
   */
  brand: brandRamp(theme?.primary_color),
  primary: {
    /*
     * BLACK is the VERA base theme's accent. A tenant's Studio `primary_color`
     * still overrides it — only the UNTHEMED default is black.
     *
     * ## This is a CONSIDERED REVERSAL of VERA-304, not a bug fix (2026-09-02)
     *
     * Read this before "fixing" it back. The colour here has now moved three
     * times — VERA-837 black -> VERA-304 purple -> this — so the argument
     * matters more than the value.
     *
     * VERA-304 (2026-08-06) set this to `basePalette.brand[600]` (#7F48DF) on
     * the framing that "VERA purple is not a fallback, it is the VERA BASE
     * THEME's accent", and that black "represented no one". Spencer has been
     * shown that reasoning in full and has decided for black anyway (user
     * directive, 2026-09-02). It is a product call about what Vera's own
     * default surface looks like, and it outranks the argument above.
     *
     * ## VERA-304's concrete trigger no longer exists
     *
     * Its stated trigger was that "the gift-flow scan loader rendered pure
     * black". That loader is `ScanTag`, and it does NOT read this line — it
     * reads `brand[600] || primary.main` (ScanTag.tsx), and `brand` is built by
     * `./brandRamp.ts` from the tenant's own colour with a `[0,0,0]` fallback.
     * So `brand[600]` has ALREADY been #000000 on an unthemed surface since
     * VERA-315 landed the derived ramp — the loader has been black this whole
     * time, independently of this line, and ScanTag's own comment says so
     * ("degrades to neutral (the default tenant primary is black)").
     *
     * In other words VERA-304 changed a line that was not the cause of the
     * symptom it cited. Setting this back to black does not reintroduce that
     * regression, because the regression was never controlled from here.
     *
     * ## What this ALIGNS, rather than breaks
     *
     * Three surfaces already default to black and disagreed with this line:
     *   - `settings/theme.ts` — the OUTER static theme, `APP_DEFAULT_PRIMARY = '#000000'`
     *   - the Studio's own colour swatches — `ThemeForm.tsx` shows `#000000`
     *   - the Studio Email-tab preview — `@repo/emails` `buttonBg: '#000000'`
     * A company that had never saved a theme therefore saw a BLACK swatch in
     * the Studio while the certificate preview beside it rendered a PURPLE
     * accent. This line was the sole source of that contradiction, so black
     * makes the editor agree with what it is previewing.
     *
     * VERA-837's original objection stays fixed either way: `text.heading`
     * below reads `theme?.primary_color` independently, so page titles never
     * took the accent.
     *
     * Merge note: VERA-315's neutral RAMP (50-950, below) is untouched and
     * orthogonal — it is greys, explicitly not brand identity.
     */
    main: theme?.primary_color || STUDIO_THEME_DEFAULTS.primary_color,
    /**
     * VERA-315: the neutral ramp, copied verbatim from `apps/platform`.
     *
     * This object REPLACES the one `...basePalette` brings in, so before this the
     * app had `primary.main` and nothing else — every `palette.primary[N]` lookup
     * in a shared `@repo/ui` component resolved to `undefined` and fell through to
     * its `gray[...]` fallback. The visible symptom was the toolbar: `Button`'s
     * `tertiary` hover paints `primary[100]`, which is #e7e7e7 on the platform and
     * a much darker grey via the fallback here, so the Filter and Sort buttons
     * flashed a heavy slab on press while the identical control on the platform
     * barely tinted.
     *
     * Fixing it at the palette rather than at the call site is the point: every
     * shared component reading `primary[N]` — buttons, inputs, menus — now behaves
     * the same in both apps, in both modes, instead of each one growing its own
     * app-specific override.
     *
     * `main` stays the TENANT's colour. The ramp is deliberately neutral greys and
     * is not brand identity; a brand's own colour continues to arrive through
     * `main` and through `brand[*]` (see `brandRamp.ts`).
     */
    50: '#f6f6f6',
    100: '#e7e7e7',
    200: '#d1d1d1',
    300: '#b0b0b0',
    400: '#888888',
    500: '#6d6d6d',
    600: '#5d5d5d',
    700: '#4f4f4f',
    800: '#454545',
    900: '#3d3d3d',
    950: '#000000',
  },
  text: {
    // Body text uses the company's Text color; headings are recolored via the
    // MuiTypography override (VERA-269) — see `headingColor` below.
    primary: theme?.text_color || STUDIO_THEME_DEFAULTS.text_color,
    /**
     * Secondary/muted body text, DERIVED from the brand's own two colours.
     *
     * ## History, and the claim that was wrong
     *
     * #7A7A7A until 2026-09-03, which measured **4.29:1 on bare white** — it
     * failed on a blank page, everywhere in the app. It was replaced with a
     * hardcoded #707070, measuring **4.95:1 on white** and 4.76:1 on the auth
     * ground (#FBFAFD).
     *
     * ⚠️ That change also asserted: "Darkening text is monotone — every surface
     * that consumes `text.secondary` gains contrast and none can lose it."
     * **That is false, and it is why this went unnoticed for two months.** It
     * holds only on a LIGHT ground. Against a dark brand background darkening
     * moves the text TOWARD the background and strictly LOSES contrast — the
     * exact opposite of the stated invariant. Measured on a dark brand ground
     * (#6E5356), the "safe" #707070 renders **1.40:1**, against a 4.5:1 floor.
     *
     * The invariant was true of every case anyone had measured — all of them
     * light — and was then generalised to "none can lose it". `text.primary` has
     * been the brand's `text_color` since VERA-269; the muted variant was simply
     * never derived alongside it.
     *
     * ## What it does now
     *
     * Mixes `text_color` toward `bg_color` for de-emphasis, then clamps so the
     * result still clears 4.5:1 against that background. The recession is kept
     * deliberately — rendering labels in the body colour outright would make
     * label and value the same weight and `ProductDetailsPanel`'s two-column
     * table would stop reading as label/value.
     *
     * On the unthemed pair this returns **#707070 exactly** (the mix constant is
     * calibrated to it — see `MUTED_MIX`), so nothing moves for a brand that has
     * not themed. `?? '#707070'` is reached only if both colours are
     * unparseable, which `parseColor` treats as "no answer" rather than throwing.
     *
     * Still a mix toward the background rather than a hue shift: this must read
     * as "muted", and shifting hue to buy contrast would read as a colour.
     */
    secondary:
      mutedTextOn(
        theme?.text_color || STUDIO_THEME_DEFAULTS.text_color,
        theme?.bg_color || STUDIO_THEME_DEFAULTS.bg_color,
      ) ?? '#707070',
    /**
     * Heading colour for h1-h4.
     *
     * A brand that HAS set a primary colour still gets its headings in it
     * (VERA-269 white-labelling is unchanged). What changed is the FALLBACK: with
     * no brand colour configured, headings previously rendered in Vera's purple
     * `#7F48DF`, which made "My Certificates" and every page title purple on the
     * default theme. Vera's own default is black, so fall through to the text
     * colour instead of the brand accent.
     */
    heading: theme?.primary_color || theme?.text_color || STUDIO_THEME_DEFAULTS.primary_color,
  } as PaletteOptions['text'],
  background: {
    // A soft grey, not pure white. White cards on a white page have only a 1px
    // border separating them, which is what made the certificates list read flat.
    // Same precedent as the VERA-837 primary-colour change: only the FALLBACK
    // moves, so a brand that sets `bg_color` still gets exactly its own surface.
    default: theme?.bg_color || STUDIO_THEME_DEFAULTS.bg_color,
    /**
     * The raised card surface (VRG-827).
     *
     * ⚠️ Until now this key was ABSENT, so MUI's own default `#fff` won — and
     * every consumer of `background.paper` rendered a white card regardless of
     * the brand. That includes `stateCardSurfaceSx`, which the certificate page
     * spreads across its state cards AND the Gift / Sell / Report tiles, so a
     * brand with a dark background shipped white slabs on it. Live, not just in
     * the Studio preview — the preview was rendering it faithfully.
     *
     * NOT `bg_color` itself. Card equal to page is a 1.00:1 boundary, which is
     * precisely the defect `CertificatePage/components/stateCardSurface.ts`
     * measured and fixed ("the card had, in the literal sense, no surface of its
     * own"). `surfaceOn` steps off the background instead — lighter, as the app
     * already does with a grey page and a white card, and as elevation
     * conventionally works on dark grounds.
     *
     * On the unthemed `#F7F7F8` it returns `#FFFFFF`, so the default is
     * unchanged to the byte.
     */
    paper: surfaceOn(theme?.bg_color || STUDIO_THEME_DEFAULTS.bg_color) ?? '#FFFFFF',
  },
  secondary: {
    /**
     * The Studio "Border color" token — and until now the one DEAD control in
     * Studio: a brand could set it, watch nothing change, and have no way to
     * find out why.
     *
     * Every consumer of it writes a border, and `index.css`'s
     * `#vera-shadow-root *` rule carries ID specificity — so each one was
     * authored, computed, and then discarded (`settings/tokens.ts` documents
     * the mechanism in full). They now carry `!important`, which is the only
     * thing that beats an ID selector, so the token finally reaches a pixel.
     *
     * ## Why the fallback moved from `#A9A9A9` to `border.subtle`
     *
     * `#A9A9A9` is a MID grey, and it was harmless only BECAUSE nothing
     * rendered it. Letting the consumers win without moving it would have
     * repainted every divider in the app from `#E4E4E7` to a much heavier
     * `#A9A9A9` on the DEFAULT theme — a visible regression for every brand
     * that has NOT set the token, shipped as a side effect of fixing it for the
     * ones that have.
     *
     * `border.subtle` is precisely what those consumers render today: it is
     * what `--border` resolves to, and the blanket rule has been forcing it on
     * them all along. So the unthemed default is byte-identical before and
     * after, and the change is strictly additive — a brand that sets the token
     * now gets it, a brand that has not sees nothing move.
     *
     * VRG-720: the literal moved to `STUDIO_THEME_DEFAULTS.secondary_color` so
     * Studio's "Border color" swatch can show it. Same value, `#E4E4E7`; it was
     * showing BLACK for an unset token before, which is the colour nobody renders.
     */
    main: theme?.secondary_color || STUDIO_THEME_DEFAULTS.secondary_color,
  },
  error: {
    200: '#FECDCA',
    300: '#FDA29B',
    main: '#FF2C2C',
    contrastText: '#fff',
  },
  warning: {
    200: '#FEDF89',
    300: '#FEC84B',
    main: '#FEC84B',
    contrastText: '#fff',
  },
  success: {
    200: '#E9F6E9',
    300: '#6CE9A6',
    main: '#08BF05',
    dark: '#E9F6E9',
    contrastText: '#fff',
  },
}) as Partial<PaletteOptions>;
